import React from "react";
import { Bell, Calendar, DollarSign, Clock, Users, LayoutDashboard, HandCoins, Monitor, UserCheck, Download, Smartphone, Wifi } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import { adminTestimonials } from "../mock";
import SEO from "../components/SEO";

const features = [
  { icon: Bell, title: "Instant Announcements", desc: "Push announcements to your entire congregation in seconds. Funeral prayers, event changes, urgent alerts, one tap." },
  { icon: Calendar, title: "Events & Programs", desc: "Create, manage, and publish events and programs. Track attendance and enrollment from your phone." },
  { icon: DollarSign, title: "Donation Oversight", desc: "View real-time donation totals, campaign performance, and transaction history on the go." },
  { icon: Clock, title: "Prayer Time Management", desc: "Set and update prayer times for your masjid. Congregation gets automatic notifications." },
  { icon: Users, title: "Community Management", desc: "Engage your community, manage memberships, and track participation, all from a single screen." },
  { icon: LayoutDashboard, title: "Live Dashboard", desc: "Key metrics at a glance: donations, active campaigns, upcoming events, and community activity." },
  { icon: HandCoins, title: "Zakat & Fundraising", desc: "Manage Zakat collections and run fundraising campaigns with built-in goal tracking." },
  { icon: Monitor, title: "Control Kiosk, Mobile POS & Signage Player", desc: "Give authorized staff complete control of your masjid’s connected hardware directly from the mobile app. Manage kiosks, mobile POS devices, and digital signage players remotely from one convenient platform. Staff can update content, monitor device activity, manage settings, and ensure every screen and payment point stays current, without needing to access each device individually." },
  { icon: UserCheck, title: "Membership Management", desc: "Easily manage member registrations, renewals, and member records, with built-in tools to suspend or revoke membership when necessary." },
];

const steps = [
  { icon: Download, title: "Download & Sign In", desc: "Install the Masjid Suite app, sign in with your admin account, and your masjid data loads instantly." },
  { icon: Smartphone, title: "Manage From Anywhere", desc: "Post announcements, update prayer times, create events, and review donations, no laptop needed." },
  { icon: Wifi, title: "Stay Connected", desc: "Your congregation sees updates in real time. One admin action reaches every community member." },
];

const quotes = adminTestimonials;

export default function AdminApp() {
  return (
    <div>
      <SEO
        title="Masjid Suite Admin App | Run Your Masjid From Your Phone"
        description="Manage announcements, donations, events, prayer times, and membership from anywhere with the free Masjid Suite Admin App, built for Imams and masjid staff."
        path="/admin-app"
      />
      <PageHero
        eyebrow="Masjid Suite Admin App"
        title="Run Your Masjid,"
        gradient="From Your Phone."
        lead={<>The Masjid Suite Admin App puts full admin control in your pocket. Announcements, donations, events, prayer times, and community management, all from one app built for Imams and Masjid administrators.</>}
        cta={<><CTAButton to="/contact">Book a Demo</CTAButton><CTAButton to="#features" variant="secondary">See Features</CTAButton></>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative flex justify-center">
              <img src="/assets/admin-app.png" alt="Masjid Suite Admin App Program screen" className="w-[280px] md:w-[320px] h-auto drop-shadow-2xl" draggable={false} />
            </div>
            <div>
              <Eyebrow>Purpose-built</Eyebrow>
              <H2 gradient="Masjid Admins">Built for Imams &</H2>
              <div className="mt-6"><Lead>Every feature is designed around how you actually manage a masjid, not a generic app repurposed for Islamic use.</Lead></div>
              <ul className="mt-8 space-y-3 text-[15px] text-[#4b5563]">
                {["Manage your masjid from anywhere, any time","Push announcements that reach every member instantly","Real-time donation and campaign tracking","Full event and program management on mobile","Prayer time updates with auto-notifications","Secure role-based access for staff"].map((t,i)=>(
                  <li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c9a34a] mt-2.5 shrink-0"/> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </Section>

      <Section id="features" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="to Manage Your Masjid">Everything You Need</H2>
            <div className="mt-6 flex justify-center"><Lead>One app replaces the group chats, spreadsheets, and phone calls.</Lead></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f,i) => {
              const Icon = f.icon;
              return (
                <GlassCard key={i}>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4 shadow-md"><Icon size={22} strokeWidth={1.8}/></div>
                  <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{f.title}</h3>
                  <p className="text-[13px] text-[#4b5563] leading-relaxed">{f.desc}</p>
                </GlassCard>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="in Minutes">Up and Running</H2>
            <div className="mt-6 flex justify-center"><Lead>No training. Set up in minutes. Download and manage.</Lead></div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s,i) => {
              const Icon = s.icon;
              return (
                <GlassCard key={i}>
                  <div className="text-[#a8862f] font-display text-4xl font-semibold mb-3">0{i+1}</div>
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><Icon size={20}/></div>
                  <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">{s.title}</h3>
                  <p className="text-[14px] text-[#4b5563] leading-relaxed">{s.desc}</p>
                </GlassCard>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14"><H2 gradient="Leaders">Trusted by Masjid</H2></div>
          <div className="grid md:grid-cols-3 gap-5">
            {quotes.map((q,i) => (
              <GlassCard key={i}>
                <div className="text-[#c9a34a] font-display text-5xl leading-none">“</div>
                <p className="text-[15px] text-[#1d1d1f] leading-relaxed font-display italic">{q.q}</p>
                <div className="mt-5 pt-4 border-t border-[#e6e7eb]">
                  <div className="font-display text-[15px] text-[#0e2408] font-semibold">{q.name}</div>
                  <div className="text-[12px] text-[#6b7280]">{q.org}</div>
                </div>
              </GlassCard>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <GlassCard hover={false} className="p-10 md:p-14 text-center">
            <Eyebrow>Full Ecosystem Sync</Eyebrow>
            <H2 gradient="All Connected.">App, Kiosk & Dashboard,</H2>
            <div className="mt-6 flex justify-center"><Lead>Every action in the Admin App syncs instantly with the Masjid Suite Dashboard and Kiosk. Post an announcement on your phone, members see it everywhere in seconds.</Lead></div>
            <div className="mt-10 flex justify-center"><CTAButton to="/contact">Book a Demo</CTAButton></div>
          </GlassCard>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-14 text-center shadow-apple-lg">
            <H2 gradient="Starting Today.">Your Masjid in Your Pocket.</H2>
            <div className="mt-6 flex justify-center"><Lead>Join masjid admins and Imams using Masjid Suite to run a more connected, more engaged community.</Lead></div>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/contact">Book a Demo</CTAButton>
              <CTAButton to="/software#apps" variant="secondary">See All Features</CTAButton>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
